<script setup lang="ts">
import dayjs, { type Dayjs } from 'dayjs'
import { ref, watch } from 'vue'
import { $t, usePrevioCore } from '@previo/core'
import { type IErrorState } from './model/ErrorState'
import Segment from './components/Segment.vue'

type Emits = {
  (e: 'next'): void
  (e: 'prev'): void
}

const props = defineProps({
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
})

const model = defineModel<Dayjs | null>()
const emits = defineEmits<Emits>()

const { UUID } = usePrevioCore()

const id = UUID.v7()

const segmentComponent = ref<InstanceType<typeof Segment>>()

const text = ref<string>('YYYY')
const buffer = ref<string>('') // holds temporary typed digits

const isValid = (): boolean => {
  if (!model.value) {
    return false
  }

  return model.value.tz().isValid() && text.value !== 'YYYY'
}

const validate = (errorState: IErrorState): void => {
  if (isValid()) {
    return
  }

  errorState.set($t('The value {value} is required', { value: 'YYYY' }))
}

const focus = (): void => {
  segmentComponent.value?.focus()
}

const ensureValid = (): void => {
  const raw = buffer.value.trim()

  if (!/^\d+$/.test(raw)) {
    return
  }

  const year = parseInt(raw, 10)

  if (!model.value) {
    return
  }

  model.value = model.value.tz().year(year)

  emits('next')
}

const onInput = (value: string): void => {
  buffer.value = (buffer.value + value).slice(-4)

  if (buffer.value.length === 4) {
    const year = parseInt(buffer.value, 10)

    if (!isNaN(year)) {
      const base =
        model.value ?? (props.defaultTime ? dayjs(props.defaultTime, 'HH:mm') : dayjs().tz())
      model.value = base.set('year', year)

      emits('next')
    }

    buffer.value = ''
  } else {
    text.value = buffer.value
  }
}

const onDelete = (): void => {
  buffer.value = ''
  text.value = 'YYYY'
}

const onAdd = (): void => {
  model.value = model.value?.tz().add(1, 'year')
}

const onSubtract = (): void => {
  model.value = model.value?.tz().subtract(1, 'year')
}

const onNext = (): void => {
  ensureValid()
  emits('next')
}

const onPrev = (): void => {
  ensureValid()
  emits('prev')
}

const onConfirm = (): void => {
  ensureValid()
}

watch(
  model,
  () => {
    if (!model.value) {
      text.value = 'YYYY'
      return
    }

    text.value = model.value.tz().format('YYYY')
  },
  { immediate: true },
)

defineExpose({
  focus,
  validate,
})
</script>

<template>
  <segment
    :id="id"
    ref="segmentComponent"
    :text="text"
    @input="onInput"
    @delete="onDelete"
    @next="onNext"
    @prev="onPrev"
    @add="onAdd"
    @subtract="onSubtract"
    @confirm="onConfirm"
  />
</template>
